import { TeammateRoleTypes } from "@repo/app-types/entities/teammatePermissions";
import { dropdownMenu } from "@repo/ui/Dropdown/dropdownMenuStyles";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconDownload,
  IconExclamationCircle,
  IconPlayerPause,
  IconPlayerPlay,
  IconUser,
  IconVolume,
  IconVolumeOff,
} from "@tabler/icons-react";
import { Duration } from "luxon";
import {
  type FC,
  type PointerEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useKeyboard, useLandmark } from "react-aria";
import {
  Button as AriaButton,
  DialogTrigger,
  Popover,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { selectTeammate } from "../../features/teammate/teammateSlice.js";
import { useAppSelector } from "../../hooks/hook.js";
import { useHasFullscreenElement } from "../../hooks/useFullscreen.js";
import { SizedContainer } from "../sizedContainer.js";
import { toggleAudioPlayback, useAudioIsPlaying } from "./audioPlayback.js";
import { AudioPlayerContext } from "./context.js";
import { findNextFragmentTime } from "./findNextFragmentTime.js";
import {
  buildLabelForRate,
  findNextPlaybackRate,
  findPreviousPlaybackRate,
  PlaybackRateSelect,
} from "./playbackRateSelect.js";
import { seekAudio, useAudioCurrentTime } from "./useAudioCurrentTime.js";
import { VolumeDialog } from "./volumeDialog.js";

interface AudioPlayerProps {
  /**
   * `overlay` styles the bar for a video surface and drops the download
   * button. The `inline` bar hides itself while anything is fullscreen, as
   * native fullscreen covers it and an overlay bar takes its place.
   */
  variant?: "inline" | "overlay";
  /** Moves focus to the bar when it mounts so the keyboard shortcuts apply. */
  autoFocus?: boolean;
  /** Reports the playback-speed and volume menus opening and closing. */
  onPopoverOpenChange?: (isOpen: boolean) => void;
}

/** Formats a media time without leading units that have no value. */
function formatPlaybackTime(seconds: number) {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  return Duration.fromMillis(wholeSeconds * 1000).toFormat(
    wholeSeconds >= 3600 ? "h:mm:ss" : "mm:ss",
  );
}

/**
 * Control bar for the provider's single audio element. Every value shown is
 * read from that element, so several bars can be mounted at once (for example
 * an inline one and a fullscreen overlay one) without disagreeing.
 */
export const AudioPlayer: FC<AudioPlayerProps> = (props) => {
  const hasFullscreenElement = useHasFullscreenElement();
  // Unmount rather than render nothing: the bar's landmark registration must
  // go with it, or landmark ordering compares against a detached ref.
  if (props.variant !== "overlay" && hasFullscreenElement) {
    return null;
  }
  return <AudioPlayerBar {...props} />;
};

const AudioPlayerBar: FC<AudioPlayerProps> = (props) => {
  const { variant = "inline", autoFocus = false, onPopoverOpenChange } = props;
  const isOverlay = variant === "overlay";
  const controlButtonClass =
    "flex size-9 items-center justify-center rounded-md bg-transparent p-0 text-(--player-fg) transition-[background-color,color] hover:bg-(--player-hover-bg) hover:text-(--player-hover-fg) disabled:text-(--player-disabled-fg)";
  const context = useContext(AudioPlayerContext);
  const nativeAudio = context.nativeAudio;
  const { timeDisplay, setTimeDisplay } = context;

  const isLoading = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("loadstart", onStoreChange);
        nativeAudio?.addEventListener("loadeddata", onStoreChange);
        nativeAudio?.addEventListener("error", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("loadstart", onStoreChange);
          nativeAudio?.removeEventListener("loadeddata", onStoreChange);
          nativeAudio?.removeEventListener("error", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () =>
      nativeAudio !== null &&
      nativeAudio.readyState < HTMLMediaElement.HAVE_CURRENT_DATA &&
      nativeAudio.networkState === HTMLMediaElement.NETWORK_LOADING,
  );

  const isError = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("loadstart", onStoreChange);
        nativeAudio?.addEventListener("error", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("loadstart", onStoreChange);
          nativeAudio?.removeEventListener("error", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => !!nativeAudio?.error,
  );

  const isPlaying = useAudioIsPlaying(nativeAudio);

  const currentTime = useAudioCurrentTime(nativeAudio);

  const duration = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("durationchange", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("durationchange", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => nativeAudio?.duration ?? NaN,
  );

  const volume = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("volumechange", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("volumechange", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => nativeAudio?.volume ?? NaN,
  );

  const playbackRate = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("ratechange", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("ratechange", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => nativeAudio?.playbackRate ?? 1,
  );

  // Value between 0 and 1 indicating where the audio is playing
  const currentFraction = isFinite(duration) ? currentTime / duration : 0;

  // Value between 0 and 1 indicating where the user is seeking (actively dragging the slider)
  const [seekFraction, setSeekFraction] = useState<number | undefined>(
    undefined,
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const currentTeammate = useAppSelector(selectTeammate);
  const currentTeammateRole = currentTeammate?.permissions.role.type;
  const canDownload =
    currentTeammateRole === TeammateRoleTypes.EDITOR ||
    currentTeammateRole === TeammateRoleTypes.REVIEWER;

  // Value between 0 and 1 indicating what we want to show in the slider
  const sliderFraction = seekFraction ?? currentFraction;

  // Time in seconds indicating where the user is seeking
  const seekTime =
    isFinite(duration) && seekFraction !== undefined
      ? seekFraction * duration
      : undefined;

  // Time in seconds we want to show in the slider
  const sliderTime = seekTime ?? currentTime;

  // TODO: Use Slider component from react-aria-components
  const activePointerIdRef = useRef<number | undefined>(undefined);
  const findSeekFraction = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const xFraction = (event.clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(xFraction, 1));
  };
  const commitSeekFraction = (fraction: number) => {
    activePointerIdRef.current = undefined;
    setSeekFraction(undefined);
    if (nativeAudio && isFinite(nativeAudio.duration)) {
      seekAudio(nativeAudio, fraction * nativeAudio.duration);
    }
  };

  const fragmentTimes = useMemo(
    () =>
      context.sources.find((source) => source.key === context.currentSourceKey)
        ?.fragmentTimes,
    [context.sources, context.currentSourceKey],
  );

  const onPlayPause = () => {
    toggleAudioPlayback(nativeAudio);
  };

  const onRewind = () => {
    if (nativeAudio) {
      seekAudio(nativeAudio, nativeAudio.currentTime - 15);
    }
  };

  const onForward = () => {
    if (!nativeAudio) {
      return;
    }

    const nextFragmentTime = fragmentTimes
      ? findNextFragmentTime(
          fragmentTimes,
          // Round up on the nearest 10 ms so we can skip past discrepancies in audio frames and floating point precision
          Math.ceil(currentTime * 100) / 100,
        )
      : undefined;

    if (nextFragmentTime !== undefined) {
      seekAudio(nativeAudio, nextFragmentTime);
    } else {
      seekAudio(nativeAudio, nativeAudio.currentTime + 15);
    }
  };

  const onDownload = async () => {
    const currentSource = context.sources.find(
      (source) => source.key === context.currentSourceKey,
    );

    if (!currentSource?.src) {
      return;
    }

    setIsDownloading(true);

    try {
      // A presigned (or provider-public) URL, so it needs no cookies.
      const response = await fetch(currentSource.src);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch audio: ${response.status} ${response.statusText}`,
        );
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      const baseTitle = currentSource.title?.trim() || "audio";
      const safeTitle = baseTitle.replace(/[\\/:*?"<>|]+/g, "-");
      link.download = safeTitle.endsWith(".mp3")
        ? safeTitle
        : `${safeTitle}.mp3`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback to opening the URL if fetch is blocked (e.g., CORS) or fails
      // Browser will use server-provided headers/filename if available
      window.open(currentSource.src, "_blank");
    } finally {
      setIsDownloading(false);
    }
  };

  // Make it easy to jump to the audio player for keyboard users (by hitting F6 or using a screen reader)
  const landmarkRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoFocus) {
      landmarkRef.current?.focus();
    }
  }, [autoFocus]);
  const { landmarkProps } = useLandmark(
    {
      role: "region",
      "aria-label": isOverlay ? "Fullscreen audio player" : "Audio player",
    },
    landmarkRef,
  );

  // Support keyboard shortcuts after jumping to or clicking on the audio player
  // TODO: Maybe make these shortcuts work for the entire dialog?
  const { keyboardProps } = useKeyboard({
    onKeyDown: (event) => {
      if (!event.isDefaultPrevented()) {
        // These keyboard shortcuts match YouTube
        switch (event.key) {
          case " ":
          case "k":
            onPlayPause();
            return;

          case "ArrowLeft":
          case "j":
            onRewind();
            return;

          case "ArrowRight":
          case "l":
            onForward();
            return;

          case "<":
            if (nativeAudio) {
              nativeAudio.playbackRate = findPreviousPlaybackRate(
                nativeAudio.playbackRate,
              );
            }
            return;

          case ">":
            if (nativeAudio) {
              nativeAudio.playbackRate = findNextPlaybackRate(
                nativeAudio.playbackRate,
              );
            }
            return;
        }
      }

      event.continuePropagation();
    },
  });

  const { keyboardProps: thumbKeyboardProps } = useKeyboard({
    onKeyDown: (event) => {
      if (!event.isDefaultPrevented()) {
        switch (event.key) {
          case " ":
            onPlayPause();
            return;

          case "ArrowLeft":
            if (nativeAudio) {
              seekAudio(nativeAudio, nativeAudio.currentTime - 1);
            }
            return;

          case "ArrowRight":
            if (nativeAudio) {
              seekAudio(nativeAudio, nativeAudio.currentTime + 1);
            }
            return;

          case "Home":
            if (nativeAudio) {
              seekAudio(nativeAudio, 0);
            }
            return;

          case "End":
            if (nativeAudio && isFinite(nativeAudio.duration)) {
              seekAudio(nativeAudio, nativeAudio.duration);
            }
            return;
        }
      }

      event.continuePropagation();
    },
  });

  const renderTimer = () => {
    if (isError) {
      return <IconExclamationCircle className="size-4 text-destructive" />;
    }

    if (!isFinite(duration) && isLoading) {
      return (
        <div className="h-4 w-28 animate-pulse rounded bg-(--player-skeleton)" />
      );
    }

    if (!isFinite(duration)) {
      return "--:-- / --:--";
    }

    const elapsed = sliderTime;
    const remaining = duration - sliderTime;
    const displayedTime = timeDisplay === "elapsed" ? elapsed : remaining;
    const prefix = timeDisplay === "remaining" ? "-" : "";
    return `${prefix}${formatPlaybackTime(
      displayedTime,
    )} / ${formatPlaybackTime(duration)}`;
  };

  return (
    <div
      ref={landmarkRef}
      data-variant={variant}
      className={twMerge(
        "w-full border-t border-border-light bg-background outline-none data-[variant=overlay]:border-transparent data-[variant=overlay]:bg-transparent",
        "[--player-disabled-fg:var(--color-gray-400)] [--player-fg:var(--color-muted-foreground)] [--player-focus:var(--color-indigo-600)] [--player-hover-bg:var(--color-gray-100)] [--player-hover-fg:var(--color-gray-600)] [--player-progress:var(--color-primary)] [--player-skeleton:var(--color-gray-300)] [--player-track:var(--color-gray-300)]",
        "data-[variant=overlay]:[--player-fg:var(--color-white)] data-[variant=overlay]:[--player-focus:var(--color-white)] data-[variant=overlay]:[--player-hover-fg:var(--color-white)] data-[variant=overlay]:[--player-progress:var(--color-white)]",
        "data-[variant=overlay]:[--player-disabled-fg:color-mix(in_srgb,var(--color-white)_40%,transparent)] data-[variant=overlay]:[--player-hover-bg:color-mix(in_srgb,var(--color-white)_15%,transparent)] data-[variant=overlay]:[--player-skeleton:color-mix(in_srgb,var(--color-white)_20%,transparent)] data-[variant=overlay]:[--player-track:color-mix(in_srgb,var(--color-white)_30%,transparent)]",
      )}
      {...landmarkProps}
      tabIndex={-1}
      {...keyboardProps}
    >
      <div className="flex w-full items-center gap-2 px-2 py-2">
        <AriaButton
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          className={twMerge("shrink-0", controlButtonClass)}
          isDisabled={!nativeAudio || context.currentSourceKey === undefined}
          onPress={onPlayPause}
        >
          {isPlaying ? (
            <IconPlayerPause className="size-5" />
          ) : (
            <IconPlayerPlay className="size-5 translate-x-[1px]" />
          )}
        </AriaButton>

        <div className="flex min-w-0 flex-1 items-center gap-2 pr-4">
          <AriaButton
            aria-label={
              timeDisplay === "elapsed"
                ? "Show time remaining"
                : "Show elapsed time"
            }
            className="inline-flex shrink-0 items-center justify-center rounded bg-transparent px-2 py-2 text-center font-mono text-xs text-(--player-fg) tabular-nums transition-[background-color] outline-none hover:bg-(--player-hover-bg) focus-visible:ring-2 focus-visible:ring-(--player-focus)"
            isDisabled={!isFinite(duration)}
            onPress={() =>
              setTimeDisplay(
                timeDisplay === "elapsed" ? "remaining" : "elapsed",
              )
            }
          >
            {renderTimer()}
          </AriaButton>

          <div
            className="group relative inline-flex h-10 w-full min-w-20 cursor-pointer touch-none items-center rounded-md outline-hidden select-none aria-hidden:hidden"
            onPointerDown={(event) => {
              if (
                !nativeAudio ||
                !isFinite(duration) ||
                event.button !== 0 ||
                !event.isPrimary ||
                activePointerIdRef.current !== undefined
              ) {
                return;
              }
              event.currentTarget.setPointerCapture(event.pointerId);
              activePointerIdRef.current = event.pointerId;
              setSeekFraction(findSeekFraction(event));
            }}
            onPointerMove={(event) => {
              if (activePointerIdRef.current === event.pointerId) {
                setSeekFraction(findSeekFraction(event));
              }
            }}
            onPointerUp={(event) => {
              if (activePointerIdRef.current === event.pointerId) {
                commitSeekFraction(findSeekFraction(event));
                event.currentTarget.releasePointerCapture(event.pointerId);
              }
            }}
            onLostPointerCapture={(event) => {
              if (activePointerIdRef.current === event.pointerId) {
                activePointerIdRef.current = undefined;
                setSeekFraction(undefined);
              }
            }}
          >
            <div className="relative h-[5px] w-full rounded-full bg-(--player-track) ring-(--player-focus) ring-offset-2 group-data-[focus]:ring-2">
              {/* <div className="absolute h-full w-[var(--slider-progress)] bg-indigo-300 will-change-[width]" /> */}
              <div
                className="absolute h-full rounded-full bg-(--player-progress) will-change-[width]"
                style={{
                  width: `${(sliderFraction * 100).toFixed(1)}%`,
                }}
              />
            </div>
            {isFinite(duration) ? (
              <div
                className="absolute top-1/2 size-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-(--player-progress) opacity-0 ring-(--player-focus) ring-offset-2 outline-hidden transition-opacity will-change-[left] group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2"
                style={{
                  left: `${(sliderFraction * 100).toFixed(1)}%`,
                }}
                // Make the slider thumb focusable and support arrow key presses
                role="slider"
                aria-label="Playback position"
                aria-valuemin={0}
                aria-valuemax={duration}
                aria-valuenow={Math.round(sliderTime)}
                aria-valuetext={`${formatPlaybackTime(
                  sliderTime,
                )} of ${formatPlaybackTime(duration)}`}
                tabIndex={0}
                {...thumbKeyboardProps}
              />
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center">
          <AriaButton
            aria-label="Rewind 15 seconds"
            className={twMerge("relative", controlButtonClass)}
            isDisabled={!nativeAudio || context.currentSourceKey === undefined}
            onPress={onRewind}
          >
            <IconArrowBackUp className="size-5" />
            <span className="absolute bottom-0.5 left-1 text-[10px]">15</span>
          </AriaButton>

          <AriaButton
            aria-label={fragmentTimes ? "Next speaker" : "Forward 15 seconds"}
            className={twMerge("relative", controlButtonClass)}
            isDisabled={!nativeAudio || context.currentSourceKey === undefined}
            onPress={onForward}
          >
            <IconArrowForwardUp className="size-5" />
            {fragmentTimes ? (
              <IconUser className="absolute right-1 bottom-1 size-3" />
            ) : (
              <span className="absolute right-1 bottom-0.5 text-[10px]">
                15
              </span>
            )}
          </AriaButton>

          <PlaybackRateSelect
            playbackRate={playbackRate}
            onOpenChange={onPopoverOpenChange}
            onPlaybackRateChange={(value) => {
              if (nativeAudio) {
                nativeAudio.playbackRate = value;
              }
            }}
          >
            <AriaButton
              className={twMerge(
                "flex flex-col items-center gap-0",
                controlButtonClass,
              )}
              aria-label="Playback speed"
              isDisabled={
                !nativeAudio || context.currentSourceKey === undefined
              }
            >
              <SizedContainer
                className="text-center text-xs font-medium"
                childrenForSize="2.0x"
              >
                {buildLabelForRate(playbackRate)}
              </SizedContainer>
            </AriaButton>
          </PlaybackRateSelect>

          <DialogTrigger onOpenChange={onPopoverOpenChange}>
            <AriaButton
              aria-label="Volume"
              className={controlButtonClass}
              isDisabled={
                !nativeAudio || context.currentSourceKey === undefined
              }
            >
              {volume >= 0.01 ? (
                <IconVolume className="size-5" />
              ) : (
                <IconVolumeOff className="size-5" />
              )}
            </AriaButton>

            <Popover className={dropdownMenu().popover()}>
              <VolumeDialog
                volume={volume}
                onVolumeChange={(value) => {
                  if (nativeAudio) {
                    nativeAudio.volume = value;
                  }
                }}
              />
            </Popover>
          </DialogTrigger>

          {canDownload && !isOverlay ? (
            <AriaButton
              aria-label="Download audio"
              className={controlButtonClass}
              isDisabled={
                !nativeAudio ||
                context.currentSourceKey === undefined ||
                isDownloading
              }
              onPress={onDownload}
            >
              {isDownloading ? (
                <Loader style={LoaderStyle.SPINNER} />
              ) : (
                <IconDownload className="size-5" />
              )}
            </AriaButton>
          ) : null}
        </div>
      </div>
    </div>
  );
};
